import { useMemo, useState } from 'react';
import { ScreenPanel, ScreensWall } from './ScreenViews';
import useSWR from 'swr';
import { useAtomValue } from 'jotai';
import { CarIcon, SearchIcon, SkullIcon } from 'lucide-react';
import { useBackendApi } from '@/hooks/fetch';
import { useOpenPlayerModal } from '@/hooks/playerModal';
import { serverMutexAtom } from '@/hooks/playerlist';
import { cn } from '@/lib/utils';
import type {
    CrashMapCell,
    CrashMapHeatmapResp,
    CrashMapLivePlayer,
    CrashMapLiveResp,
} from '@shared/vantaCrashMapTypes';
import GtaMap, { type MapFocusRequest, type MapPointerInfo } from './GtaMap';
import HeatmapLayer, { smoothRadius } from './HeatmapLayer';
import HeatmapPanel, { type HeatmapFilters } from './HeatmapPanel';
import CellDropsDialog from './CellDropsDialog';
import { gtaToScreen, metersToScreen, type MapView } from './mapProjection';

type Mode = 'live' | 'screens' | 'heatmap';
type HeatmapOk = Exclude<CrashMapHeatmapResp, { error: string }>;

const LIVE_REFRESH_MS = 2000;

const toLocalInput = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultFilters = (): HeatmapFilters => ({
    range: '7d',
    customFrom: toLocalInput(new Date(Date.now() - 7 * 24 * 3600_000)),
    customTo: toLocalInput(new Date()),
    categories: ['timeout', 'crash', 'security', 'unknown'],
    normalize: false,
    excludeMass: true,
    cell: 50,
    style: 'smooth',
});

const filtersToQuery = (f: HeatmapFilters) => {
    const params: Record<string, string> = {
        range: f.range,
        categories: f.categories.join(','),
        cell: String(f.cell),
        normalize: f.normalize ? '1' : '0',
        excludeMass: f.excludeMass ? '1' : '0',
    };
    if (f.range === 'custom') {
        params.from = String(new Date(f.customFrom).getTime() || 0);
        params.to = String(new Date(f.customTo).getTime() || 0);
    }
    return params;
};


//================================================================
// Joueurs en direct
//================================================================
const playerColor = (p: CrashMapLivePlayer) => {
    if (!p.alive) return '#FF3E26';
    if (p.vehicle) return '#406FE6';
    return '#39E673';
};

function PlayerMarkers({ view, players, onOpen }: {
    view: MapView;
    players: CrashMapLivePlayer[];
    onOpen: (p: CrashMapLivePlayer) => void;
}) {
    return (
        <div className="absolute inset-0 z-10 pointer-events-none">
            {players.map((p) => {
                const { sx, sy } = gtaToScreen(view, p.x, p.y);
                if (sx < -20 || sy < -20 || sx > view.width + 20 || sy > view.height + 20) return null;
                return (
                    <button
                        key={p.id}
                        type="button"
                        className="group absolute pointer-events-auto -translate-x-1/2 -translate-y-1/2 p-1 cursor-pointer"
                        style={{ left: sx, top: sy }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => onOpen(p)}
                        data-player-id={p.id}
                    >
                        <span
                            className="block size-2.5 rounded-full border border-black/70"
                            style={{ backgroundColor: playerColor(p) }}
                        />
                        <span className="absolute left-1/2 bottom-full mb-0.5 -translate-x-1/2 whitespace-nowrap rounded border bg-card px-1.5 py-0.5 text-xs text-foreground opacity-0 group-hover:opacity-100">
                            [{p.id}] {p.name}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function LivePlayersPanel({ players, stale, onFocus, onOpen }: {
    players: CrashMapLivePlayer[];
    stale: boolean;
    onFocus: (p: CrashMapLivePlayer) => void;
    onOpen: (p: CrashMapLivePlayer) => void;
}) {
    const [filter, setFilter] = useState('');
    const filtered = useMemo(() => {
        const f = filter.trim().toLowerCase();
        const sorted = [...players].sort((a, b) => a.id - b.id);
        if (!f) return sorted;
        return sorted.filter((p) => p.name.toLowerCase().includes(f) || String(p.id).includes(f));
    }, [players, filter]);

    return (
        <div className="flex flex-col h-full">
            <div className="p-4 pb-2 space-y-3">
                <div className="flex items-baseline justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Players</h3>
                    <span className="font-mono text-sm">{stale ? '—' : players.length}</span>
                </div>
                <div className="relative">
                    <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                    <input
                        className="h-8 w-full rounded-md border bg-background pl-7 pr-2 text-sm"
                        placeholder="Filter by name or ID"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>
                <div className="flex gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-[#39E673]" />On foot</span>
                    <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-[#406FE6]" />Vehicle</span>
                    <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-[#FF3E26]" />Dead</span>
                </div>
            </div>
            <div className="grow overflow-y-auto px-2 pb-2">
                {filtered.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted">
                        <button type="button" className="flex grow items-center gap-2 min-w-0 text-left" onClick={() => onFocus(p)} title="Show on map">
                            <span className="font-mono text-xs text-muted-foreground w-8 shrink-0">{p.id}</span>
                            <span className="truncate">{p.name}</span>
                        </button>
                        {p.vehicle && <CarIcon className="size-3.5 shrink-0 text-muted-foreground" />}
                        {!p.alive && <SkullIcon className="size-3.5 shrink-0 text-destructive" />}
                        <button type="button" className="text-xs text-muted-foreground hover:text-foreground shrink-0" onClick={() => onOpen(p)}>
                            Info
                        </button>
                    </div>
                ))}
                {!filtered.length && (
                    <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                        {stale ? 'No data from the server.' : 'No player.'}
                    </p>
                )}
            </div>
        </div>
    );
}


//================================================================
// Page
//================================================================
export default function LiveMapPage() {
    const [mode, setMode] = useState<Mode>('live');
    const [watchedId, setWatchedId] = useState<number | null>(null);
    const [filters, setFiltersState] = useState<HeatmapFilters>(defaultFilters);
    const [focus, setFocus] = useState<MapFocusRequest | null>(null);
    const [selectedCell, setSelectedCell] = useState<CrashMapCell | null>(null);
    const [hoverCell, setHoverCell] = useState<CrashMapCell | null>(null);
    const serverMutex = useAtomValue(serverMutexAtom);
    const openPlayerModal = useOpenPlayerModal();
    const setFilters = (upd: Partial<HeatmapFilters>) => setFiltersState((prev) => ({ ...prev, ...upd }));

    //Joueurs en direct
    const liveApi = useBackendApi<CrashMapLiveResp>({ method: 'GET', path: '/vanta/crashmap/live' });
    const liveSwr = useSWR(mode === 'live' || mode === 'screens' ? '/vanta/crashmap/live' : null, async () => {
        const data = await liveApi({});
        if (!data) throw new Error('empty_response');
        if ('error' in data) throw new Error(String((data as any).error));
        return data;
    }, { refreshInterval: LIVE_REFRESH_MS, revalidateOnFocus: false });
    const livePlayers = liveSwr.data?.players ?? [];
    const liveStale = !liveSwr.data || liveSwr.data.stale;
    //joueur dont l'écran est affiché (disparaît du panneau s'il se déconnecte)
    const watchedPlayer = watchedId === null ? null : livePlayers.find((p) => p.id === watchedId) ?? null;

    //Heatmap
    const queryParams = useMemo(() => filtersToQuery(filters), [filters]);
    const heatmapApi = useBackendApi<CrashMapHeatmapResp>({ method: 'GET', path: '/vanta/crashmap/heatmap' });
    const heatSwr = useSWR(
        mode === 'heatmap' ? `/vanta/crashmap/heatmap?${new URLSearchParams(queryParams)}` : null,
        async () => {
            const data = await heatmapApi({ queryParams });
            if (!data) throw new Error('empty_response');
            if ('error' in data) throw new Error(data.error);
            return data as HeatmapOk;
        },
        { revalidateOnFocus: false, keepPreviousData: true },
    );
    const heat = heatSwr.data;

    const openPlayer = (p: CrashMapLivePlayer) => {
        if (!serverMutex) return;
        openPlayerModal({ mutex: serverMutex, netid: p.id });
    };

    //Recherche de la cellule sous le curseur
    const findCell = (info: MapPointerInfo) => {
        if (!heat || !heat.cells.length) return null;
        const cx = Math.floor(info.x / heat.cell);
        const cy = Math.floor(info.y / heat.cell);
        const exact = heat.cells.find((c) => c.cx === cx && c.cy === cy);
        if (exact) return exact;
        if (filters.style !== 'smooth') return null;
        //en mode Smooth, la tache déborde de la cellule : on prend la plus proche
        const maxDist = Math.max(smoothRadius(info.view, heat.cell) * 0.7, metersToScreen(info.view, heat.cell));
        let best: CrashMapCell | null = null;
        let bestDist = Infinity;
        for (const c of heat.cells) {
            const { sx, sy } = gtaToScreen(info.view, c.x, c.y);
            const d = Math.hypot(sx - info.sx, sy - info.sy);
            if (d < maxDist && d < bestDist) {
                best = c;
                bestDist = d;
            }
        }
        return best;
    };

    const onMapClick = (info: MapPointerInfo) => {
        if (mode !== 'heatmap') return;
        const cell = findCell(info);
        if (cell) setSelectedCell(cell);
    };
    const onMapHover = (info: MapPointerInfo | null) => {
        if (mode !== 'heatmap') return;
        const cell = info ? findCell(info) : null;
        setHoverCell((prev) => (prev?.cx === cell?.cx && prev?.cy === cell?.cy ? prev : cell));
    };

    const statusText = mode === 'heatmap' && hoverCell
        ? `${hoverCell.count} drop${hoverCell.count === 1 ? '' : 's'}${filters.normalize ? ` · ${hoverCell.weight.toFixed(2)}/h` : ''} · click for details`
        : mode === 'live' && !liveStale
            ? `${livePlayers.length} player${livePlayers.length === 1 ? '' : 's'}`
            : undefined;

    const heatEmpty = mode === 'heatmap' && heat && !heat.cells.length && !heatSwr.error;

    return (
        <div className="flex flex-col h-contentvh w-full gap-3">
            {/* En-tête */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-2 md:px-0">
                <div>
                    <h1 className="text-2xl font-semibold leading-tight">Live Map</h1>
                    <p className="text-sm text-muted-foreground">
                        {mode === 'live'
                            ? 'Player positions, refreshed every 2 seconds. Click a player to see their screen live.'
                            : mode === 'screens'
                            ? 'Live screens of every connected player, refreshed every few seconds.'
                            : 'Where players disconnect, based on their last known position.'}
                    </p>
                </div>
                <div className="flex rounded-md border overflow-hidden text-sm">
                    {([['live', 'Live players'], ['screens', 'Screens'], ['heatmap', 'Crash heatmap']] as const).map(([m, label], i) => (
                        <button
                            key={m}
                            type="button"
                            className={cn(
                                'px-3 py-1.5',
                                i > 0 && 'border-l',
                                mode === m ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted',
                            )}
                            onClick={() => {
                                setMode(m);
                                setHoverCell(null);
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Carte + panneau */}
            <div className="flex grow min-h-0 flex-col lg:flex-row border bg-card md:rounded-xl overflow-hidden">
                {mode === 'screens' ? (
                    <div className="grow min-h-[360px] flex flex-col overflow-hidden">
                        <ScreensWall
                            players={liveStale ? [] : livePlayers}
                            onSelect={(p) => {
                                setWatchedId(p.id);
                                setMode('live');
                                setFocus({ x: p.x, y: p.y, seq: Date.now() });
                            }}
                        />
                    </div>
                ) : (
                <GtaMap
                    className="grow min-h-[360px]"
                    onMapClick={onMapClick}
                    onMapHover={onMapHover}
                    statusText={statusText}
                    focus={focus}
                >
                    {(view) => (
                        <>
                            {mode === 'live' && (
                                <PlayerMarkers view={view} players={livePlayers} onOpen={(p) => setWatchedId(p.id)} />
                            )}
                            {mode === 'heatmap' && heat && (
                                <HeatmapLayer
                                    view={view}
                                    cells={heat.cells}
                                    maxWeight={heat.maxWeight}
                                    cellSize={heat.cell}
                                    style={filters.style}
                                    selected={selectedCell}
                                />
                            )}
                            {heatEmpty && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                                    <p className="rounded-md border bg-card px-4 py-2 text-sm text-muted-foreground">
                                        No located drop in this range for the selected categories.
                                    </p>
                                </div>
                            )}
                            {mode === 'heatmap' && heatSwr.error && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                                    <p className="rounded-md border bg-card px-4 py-2 text-sm text-destructive">
                                        {(heatSwr.error as Error).message}
                                    </p>
                                </div>
                            )}
                            {mode === 'live' && liveSwr.data && liveStale && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                                    <p className="rounded-md border bg-card px-4 py-2 text-sm text-muted-foreground">
                                        No position received from the server. Is it online?
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </GtaMap>
                )}

                <aside className="lg:w-80 shrink-0 border-t lg:border-t-0 lg:border-l overflow-y-auto max-h-[50vh] lg:max-h-none">
                    {mode === 'live' || mode === 'screens' ? (
                        watchedPlayer ? (
                            <ScreenPanel
                                key={watchedPlayer.id}
                                player={watchedPlayer}
                                serverMutex={serverMutex ?? null}
                                onBack={() => setWatchedId(null)}
                                onOpenInfo={openPlayer}
                            />
                        ) : (
                            <LivePlayersPanel
                                players={livePlayers}
                                stale={liveStale}
                                onOpen={openPlayer}
                                onFocus={(p) => {
                                    setFocus({ x: p.x, y: p.y, seq: Date.now() });
                                    setWatchedId(p.id);
                                }}
                            />
                        )
                    ) : (
                        <HeatmapPanel
                            filters={filters}
                            setFilters={setFilters}
                            summary={heat?.summary}
                            isLoading={heatSwr.isLoading || heatSwr.isValidating}
                            error={heatSwr.error ? (heatSwr.error as Error).message : undefined}
                        />
                    )}
                </aside>
            </div>

            <CellDropsDialog
                cell={selectedCell}
                cellSize={heat?.cell ?? filters.cell}
                queryParams={queryParams}
                onClose={() => setSelectedCell(null)}
            />
        </div>
    );
}
